import FilterListIcon from '@mui/icons-material/FilterList';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Button, Checkbox, IconButton, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Conclusions,
  EvaluationStatus,
  FilterTypes,
  clearFilter,
  selectConclusionFilters,
  selectContactFilters,
  selectFilterConclusion,
  selectFilterContacts,
  selectFilterStatus,
  selectSelectedContacts,
  selectStatusFilters,
  setFilter,
} from '../../redux/reducers/assessmentReducer';
import { selectPersonDetails } from '../../redux/reducers/organizationReducer';

function ContactItem({ personId, checked, handleChange }) {
  const details = useSelector((state) => selectPersonDetails(state, personId));
  return (
    <MenuItem onClick={() => handleChange(FilterTypes.CONTACT, personId)}>
      <Checkbox variant="checkbox" checked={checked} />
      <ListItemText>{details.name ?? details.firstName ?? '?'}</ListItemText>
    </MenuItem>
  );
}

function FilterBar() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const contactList = useSelector(selectSelectedContacts);
  const filterStatus = useSelector(selectFilterStatus);
  const filterContact = useSelector(selectFilterContacts);
  const filterConclusion = useSelector(selectFilterConclusion);
  const statusFilters = useSelector(selectStatusFilters);
  const contactFilters = useSelector(selectContactFilters);
  const conclusionFilters = useSelector(selectConclusionFilters);
  const [anchor, setAnchor] = useState(null);
  const [contactFilterOpen, setContactFilterOpen] = useState(false);
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [conclusionFilterOpen, setConclusionFilterOpen] = useState(false);

  const style = {
    iconFilter: {
      ml: '-14px',
      mt: '-30px',
      width: '20px',
      height: '20px',
      backgroundColor: 'white',
      ':hover': { backgroundColor: 'white' },
    },
  };

  const handleClose = () => {
    setAnchor(null);
    setContactFilterOpen(false);
    setStatusFilterOpen(false);
    setConclusionFilterOpen(false);
  };

  const handleContact = (event) => {
    setAnchor(event.currentTarget);
    dispatch(
      setFilter({
        type: FilterTypes.CONTACT,
        value: [...contactFilters],
      }),
    );
    setContactFilterOpen(true);
  };

  const handleStatus = (event) => {
    setAnchor(event.currentTarget);
    dispatch(
      setFilter({
        type: FilterTypes.STATUS,
        value: [...statusFilters],
      }),
    );
    setStatusFilterOpen(true);
  };

  const handleConclusion = (event) => {
    setAnchor(event.currentTarget);
    dispatch(
      setFilter({
        type: FilterTypes.CONCLUSION,
        value: [...conclusionFilters],
      }),
    );
    setConclusionFilterOpen(true);
  };

  const handleChange = (type, value) => {
    switch (type) {
      case FilterTypes.CONCLUSION:
        if (conclusionFilters.includes(value)) {
          dispatch(
            setFilter({
              type: FilterTypes.CONCLUSION,
              value: [...conclusionFilters.filter((x) => x !== value)],
            }),
          );
        } else {
          dispatch(
            setFilter({
              type: FilterTypes.CONCLUSION,
              value: [...conclusionFilters, value],
            }),
          );
        }
        break;
      case FilterTypes.STATUS:
        if (statusFilters.includes(value)) {
          dispatch(
            setFilter({
              type: FilterTypes.STATUS,
              value: [...statusFilters.filter((x) => x !== value)],
            }),
          );
        } else {
          dispatch(
            setFilter({
              type: FilterTypes.STATUS,
              value: [...statusFilters, value],
            }),
          );
        }
        break;
      case FilterTypes.CONTACT:
        if (contactFilters.includes(value)) {
          dispatch(
            setFilter({
              type: FilterTypes.CONTACT,
              value: [...contactFilters.filter((x) => x !== value)],
            }),
          );
        } else {
          dispatch(
            setFilter({
              type: FilterTypes.CONTACT,
              value: [...contactFilters, value],
            }),
          );
        }
        break;
      default:
        // no-op
        break;
    }
  };

  const handleClear = (type) => {
    dispatch(clearFilter(type));
  };

  return (
    // useFlexGap and the div elements are here so that clear filter -buttons align correctly
    <Stack direction="row" useFlexGap alignItems="center" spacing={2}>
      <Typography variant="subtitle2">{t('assessment.filter.filters')}</Typography>
      <div>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={(event) => handleContact(event)}
          id="contact-filter-button"
          aria-controls={contactFilterOpen ? 'contactMenu' : undefined}
          aria-haspopup="true"
          aria-expanded={contactFilterOpen ? 'true' : undefined}
          startIcon={<FilterListIcon />}
        >
          {t('assessment.filter.byContact')}
        </Button>
        <Menu id="contactMenu" anchorEl={anchor} open={contactFilterOpen} onClose={() => handleClose()}>
          {contactList.map((person) => (
            <ContactItem
              id={`filter-by-${person}`}
              key={person}
              checked={contactFilters.includes(person)}
              personId={person}
              handleChange={handleChange}
            />
          ))}
          <MenuItem key="contactUnassigned" onClick={() => handleChange(FilterTypes.CONTACT, 'unassigned')}>
            <Checkbox id='contact-unassigned' variant="checkbox" checked={contactFilters.includes('unassigned')} />
            <ListItemText>{t('assessment.filter.unassigned')}</ListItemText>
          </MenuItem>
        </Menu>
        {filterContact && (
          <IconButton id='clear-contact-filter' sx={style.iconFilter} onClick={() => handleClear(FilterTypes.CONTACT)}>
            <HighlightOffIcon />
          </IconButton>
        )}
      </div>
      <div>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={(event) => handleStatus(event)}
          id="status-filter-button"
          aria-controls={statusFilterOpen ? 'statusMenu' : undefined}
          aria-haspopup="true"
          aria-expanded={statusFilterOpen ? 'true' : undefined}
          startIcon={<FilterListIcon />}
        >
          {t('assessment.filter.byStatus')}
        </Button>
        <Menu id="statusMenu" anchorEl={anchor} open={statusFilterOpen} onClose={() => handleClose()}>
          {EvaluationStatus.list().map((status) => (
            <MenuItem key={status} onClick={() => handleChange(FilterTypes.STATUS, status)}>
              <Checkbox id={`filter-by-${status}`} variant="checkbox" checked={statusFilters?.includes(status) ?? true} />
              <ListItemText>{t(`control.evaluation.statusType.${status}`)}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
        {filterStatus && (
          <IconButton id='clear-status.filter' sx={style.iconFilter} onClick={() => handleClear(FilterTypes.STATUS)}>
            <HighlightOffIcon />
          </IconButton>
        )}
      </div>
      <div>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={(event) => handleConclusion(event)}
          id="conclusion-filter-button"
          aria-controls={conclusionFilterOpen ? 'conclusionMenu' : undefined}
          aria-haspopup="true"
          aria-expanded={conclusionFilterOpen ? 'true' : undefined}
          startIcon={<FilterListIcon />}
        >
          {t('assessment.filter.byConclusion')}
        </Button>
        <Menu id="conclusionMenu" anchorEl={anchor} open={conclusionFilterOpen} onClose={() => handleClose()}>
          {Conclusions.list().map((conclusion) => (
            <MenuItem key={conclusion} onClick={() => handleChange(FilterTypes.CONCLUSION, conclusion)}>
              <Checkbox id={`filter-by-${conclusion}`} variant="checkbox" checked={conclusionFilters?.includes(conclusion) ?? true} />
              <ListItemText>{t(`control.evaluation.conclusion.${conclusion}`)}</ListItemText>
            </MenuItem>
          ))}
          <MenuItem key="undefined" onClick={() => handleChange(FilterTypes.CONCLUSION, 'undefined')}>
            <Checkbox id='conclusion-undefined' variant="checkbox" checked={conclusionFilters?.includes('undefined') ?? true} />
            <ListItemText>{t('assessment.filter.undefined')}</ListItemText>
          </MenuItem>
        </Menu>
        {filterConclusion && (
          <IconButton id='clear-conclusion-filter' sx={style.iconFilter} onClick={() => handleClear(FilterTypes.CONCLUSION)}>
            <HighlightOffIcon />
          </IconButton>
        )}
      </div>
    </Stack>
  );
}

export default FilterBar;
